// THE STAFF-SIDE CROPPER for the three image inputs on the Apple onboarding document.
//
// ⚠️ WHY THIS IS A CUSTOM FIELD RATHER THAN A `storage:` PROPERTY. A FireCMS `storage:` property
// uploads the moment a file is selected, so there is no point at which a crop could be inserted. The
// order this field owns — pick → measure → refuse or crop → render → upload → set `{ fileName,
// imageUrl }` — is the whole reason it exists. Nothing here uploads the original and crops
// afterwards: that leaves two objects in Storage and an ambiguous "which one is live?".
//
// ⚠️ WHY A CROPPER AT ALL. Apple refuses images whose geometry does not fit the surface, and the
// backend refuses them too and says why — it never crops, pads or resizes
// (`delegated-media/image-geometry.ts:22-35`). That is deliberate: server-side cropping a clinic
// photo can silently cut off a face, the signage or the logo, and nobody finds out until the live
// place card, days later, after Apple's multi-day review. So the framing decision is made HERE, by
// the person who chose the photo, against the same numbers the backend enforces. The machine only
// checks.
//
// ⚠️ THE BACKEND GATE STAYS. This is a convenience that moves refusals earlier and makes them fixable
// in place. It is not a replacement, and every number it targets is transcribed from that gate — see
// the header of `apple_image_crop.utils.ts`.
//
// ⚠️ EVERY DECISION IS IN `apple_image_crop.utils.ts`, NOT HERE. This component holds the browser
// mechanics — a file input, an ImageBitmap, a canvas, an upload — and asks the pure module what to
// do at each step. That split is what lets the arithmetic be exercised without a browser, which
// matters more than usual in a package with no test runner.
//
// ⚠️ THE WRITE BOUNDARY IS UNTOUCHED. This field calls `setValue`, which puts the pair into the FORM;
// the document write still happens through the collection's single save path and its `onPreSave`
// strip. `useStorageSource` is Firebase STORAGE, not Firestore — there is no `saveEntity` and no
// `firebase/firestore` import in this file.

import {
  FieldHelperText,
  FieldProps,
  LabelWithIconAndTooltip,
  useStorageSource,
} from "@firecms/core";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ImageIcon,
  LoadingButton,
  Slider,
  Typography,
} from "@firecms/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import Cropper, { Area } from "react-easy-crop";

import {
  AppleImageCropFieldProps,
  AppleImageMimeType,
  AppleImageSurface,
  AppleImageValue,
  PixelSize,
} from "./apple_image_crop.types";
import {
  acceptedFilesFor,
  buildUploadNames,
  cropAspectChoicesFor,
  cropAspectFor,
  describeExport,
  evaluateCrop,
  evaluateSource,
  evaluateUploadType,
  maxZoomFor,
  normalizeCropRect,
  outputMimeTypeFor,
  requirementFor,
  requiresCrop,
  resolveOutputSize,
  sniffImageMimeType,
  storagePathFor,
  surfaceLabel,
  verifyOutput,
} from "./apple_image_crop.utils";

/** JPEG quality for the re-encode. High enough that Apple's human quality review sees no artefacts. */
const JPEG_QUALITY = 0.92;

/** How many bytes of the header the format sniffer needs. */
const SNIFF_BYTES = 1024;

/**
 * The orientation-normalised source, once the file has been read and measured.
 *
 * ⚠️ `bitmap` IS THE ONE SOURCE OF TRUTH FOR PIXELS, and that is how EXIF orientation is handled.
 * See `loadSource`.
 */
interface LoadedSource {
  bitmap: ImageBitmap;
  size: PixelSize;
  previewUrl: string;
  sniffed: string;
  originalName: string;
  maxZoom: number;
}

/**
 * ⚠️ A CROP-STAGE FAILURE STAYS IN THE CROP STAGE, and that is what makes the refusal copy honest.
 *
 * These messages tell staff to do something — "zoom out and select more of the image", "zoom in to
 * select a smaller area". An earlier shape of this component answered a failed crop by switching to
 * `refused`, which closed the dialog and released the source, so every one of those instructions was
 * impossible to follow by the time it was read. The fix is not to soften the copy: it is to leave the
 * dialog open with the source alive, so the instruction is actually available. `notice` is that
 * message, and `busyNote` keeps the dialog up during the crop and upload rather than flickering out
 * to a spinner and back.
 *
 * `refused` therefore covers only failures reached BEFORE a crop box exists — a bad format, a source
 * too small to use — where there is genuinely nothing to adjust and no dialog to stay in.
 */
type Stage =
  | { kind: "idle" }
  | { kind: "busy"; note: string }
  | { kind: "cropping"; source: LoadedSource; notice?: string; busyNote?: string }
  | { kind: "refused"; reason: string };

/**
 * Reads a file into an orientation-normalised `ImageBitmap` plus a preview URL derived from that very
 * bitmap.
 *
 * ⚠️ THIS IS THE EXIF FIX, AND IT IS STRUCTURAL RATHER THAN A CORRECTION. A phone photo carries an
 * orientation flag; draw the file naively to a canvas and the crop comes out rotated or mirrored
 * relative to what staff framed. The usual patch — read the EXIF tag and counter-rotate — is fragile
 * because browsers ALSO apply the flag themselves when rendering `<img>` and, in modern engines, when
 * `drawImage` is handed an `HTMLImageElement`. Correcting on top of that double-applies it.
 *
 * So orientation is resolved ONCE, at the boundary: `createImageBitmap(..., { imageOrientation:
 * "from-image" })` returns pixels already in display order, with the flag consumed. The preview the
 * staff member frames against is rendered FROM that bitmap, and the crop is cut FROM the same bitmap.
 * Both live in one coordinate space that no longer has an orientation flag in it, so there is nothing
 * left to disagree about — rather than two spaces kept in sync by hand.
 */
const loadSource = async (
  surface: AppleImageSurface,
  file: File,
  sniffed: string,
): Promise<{ source: LoadedSource } | { refusal: string }> => {
  // ⚠️ Decoded straight from the `File`, which IS a `Blob`. There is deliberately no
  // `file.arrayBuffer()` on this path: reading the whole file to wrap it in a `new Blob` copied every
  // byte for nothing, since `createImageBitmap` identifies the image from its CONTENT and ignores the
  // declared MIME type — which is also why a HEIC renamed `.jpg` cannot sneak through here.
  const bitmap = await createImageBitmap(file, {
    imageOrientation: "from-image",
  });
  const size: PixelSize = { width: bitmap.width, height: bitmap.height };

  const verdict = evaluateSource(surface, size);
  if (!verdict.ok) {
    bitmap.close();
    return { refusal: verdict.reason };
  }

  // The preview is a re-encode of the normalised bitmap, NOT the original file — so what staff see is
  // provably the same pixels the crop will be cut from.
  const previewCanvas = document.createElement("canvas");
  previewCanvas.width = size.width;
  previewCanvas.height = size.height;
  const previewContext = previewCanvas.getContext("2d");
  if (!previewContext) {
    bitmap.close();
    return { refusal: "This browser did not provide a 2D canvas, so the image cannot be cropped." };
  }
  previewContext.drawImage(bitmap, 0, 0);

  const previewBlob = await new Promise<Blob | null>((resolve) =>
    previewCanvas.toBlob(resolve, "image/png"),
  );
  // Release the intermediate canvas; the bitmap is what is kept.
  previewCanvas.width = 0;
  previewCanvas.height = 0;
  if (!previewBlob) {
    bitmap.close();
    return { refusal: "The image could not be prepared for cropping. Try re-exporting the file." };
  }

  return {
    source: {
      bitmap,
      size,
      previewUrl: URL.createObjectURL(previewBlob),
      sniffed,
      originalName: file.name,
      maxZoom: maxZoomFor(surface, size),
    },
  };
};

/**
 * Renders one crop to a Blob at the resolved output resolution.
 *
 * ⚠️ `toBlob` IS ASYNCHRONOUS AND CAN RETURN `null` (an over-large canvas, or an encoder the browser
 * declines). That path is handled rather than assumed — a `null` here without a check becomes a
 * `TypeError` deep in the upload and a staff member with no idea what happened.
 */
const renderCrop = async (
  surface: AppleImageSurface,
  source: LoadedSource,
  croppedAreaPixels: Area,
): Promise<
  { blob: Blob; mimeType: AppleImageMimeType; output: PixelSize } | { refusal: string }
> => {
  const crop = normalizeCropRect(croppedAreaPixels, source.size);

  const cropVerdict = evaluateCrop(surface, crop);
  if (!cropVerdict.ok) return { refusal: cropVerdict.reason };

  const output = resolveOutputSize(surface, crop);
  const outputVerdict = verifyOutput(surface, output);
  if (!outputVerdict.ok) return { refusal: outputVerdict.reason };

  const canvas = document.createElement("canvas");
  canvas.width = output.width;
  canvas.height = output.height;
  const context = canvas.getContext("2d");
  if (!context) {
    return { refusal: "This browser did not provide a 2D canvas, so the crop cannot be rendered." };
  }

  const mimeType = outputMimeTypeFor(source.sniffed);
  // ⚠️ A fresh canvas is TRANSPARENT, and JPEG cannot store transparency — an unfilled canvas exported
  // as JPEG renders every transparent pixel BLACK. Filling white first makes the flattening explicit
  // and matches what the field tells staff (`describeExport`). A no-op for a JPEG source, which has no
  // alpha; the case that matters is a PNG with transparency whose own format is somehow JPEG.
  if (mimeType === "image/jpeg") {
    context.fillStyle = "#FFFFFF";
    context.fillRect(0, 0, output.width, output.height);
  }
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(
    source.bitmap,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    output.width,
    output.height,
  );

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, mimeType, mimeType === "image/jpeg" ? JPEG_QUALITY : undefined),
  );
  canvas.width = 0;
  canvas.height = 0;

  if (!blob) {
    return {
      refusal:
        "The browser could not encode the cropped image, so nothing was uploaded. This usually means the selected area is very large — zoom in to select a smaller area, then try again.",
    };
  }
  return { blob, mimeType, output };
};

/**
 * One image input on the onboarding document: `{ fileName, imageUrl }`, produced by cropping.
 *
 * The surface arrives through `customProps` because the three inputs differ ONLY in their geometry
 * rules, and those rules are keyed by surface in the pure module. One component, three
 * configurations — rather than three near-copies that could drift on the numbers that must not drift.
 */
export const AppleImageCropField = (
  props: FieldProps<AppleImageValue, AppleImageCropFieldProps>,
) => {
  const {
    value,
    setValue,
    propertyKey,
    property,
    includeDescription,
    showError,
    error,
    disabled,
    customProps,
  } = props;
  const surface = customProps.surface;

  const storageSource = useStorageSource();
  const [stage, setStage] = useState<Stage>({ kind: "idle" });
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  // ⚠️ STATE, not a ref: the dialog PRINTS the resolution this crop will produce, and that sentence
  // has to change as the crop does. `onCropComplete` fires at the end of an interaction rather than
  // per frame, so this is a handful of renders per drag, not a render loop.
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  /**
   * The crop shape currently framed.
   *
   * ⚠️ FOR THE THREE LOCKED SURFACES THIS NEVER CHANGES FROM `cropAspectFor(surface)`. They declare no
   * `cropAspectChoices`, so `aspectChoices` below is empty, no picker renders, and nothing can call
   * `selectAspect` — the value handed to `Cropper` is byte-for-byte the one it received before this
   * state existed. Only `galleryPhoto`, whose Apple rule is a RANGE rather than a ratio, ever moves it.
   */
  const [aspect, setAspect] = useState(() => cropAspectFor(surface));
  const inputRef = useRef<HTMLInputElement>(null);

  // Object URLs and ImageBitmaps are not garbage-collected on their own; a staff member trying five
  // photos would otherwise leak all five.
  const sourceRef = useRef<LoadedSource | null>(null);
  const releaseSource = useCallback(() => {
    const current = sourceRef.current;
    if (!current) return;
    URL.revokeObjectURL(current.previewUrl);
    current.bitmap.close();
    sourceRef.current = null;
  }, []);
  useEffect(() => releaseSource, [releaseSource]);

  const accepted = acceptedFilesFor(surface).join(",");
  const label = surfaceLabel(surface);
  /** Empty for every aspect-LOCKED surface, which is what suppresses the picker entirely. */
  const aspectChoices = cropAspectChoicesFor(surface);

  /**
   * Switches the crop shape, and RESETS the framing.
   *
   * ⚠️ THE RESET IS NOT TIDINESS. `react-easy-crop` keeps the crop offset and zoom across an `aspect`
   * change, and the previous zoom may exceed the new shape's headroom — which would leave the box
   * selecting fewer pixels than Apple's minimum, with the slider showing a value it no longer permits.
   * Zoom returns to 1 and `croppedArea` is dropped so the printed output size cannot describe a crop
   * that no longer exists.
   */
  const selectAspect = useCallback((next: number) => {
    setAspect(next);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedArea(null);
  }, []);

  /** Uploads a finished Blob and puts the pair on the form. The ONE place `setValue` is called. */
  const uploadAndSet = useCallback(
    async (blob: Blob, mimeType: AppleImageMimeType, originalName: string, output?: PixelSize) => {
      const names = buildUploadNames({
        originalName,
        surface,
        mimeType,
        uniqueSuffix: crypto.randomUUID(),
      });
      const file = new File([blob], names.storageFileName, { type: mimeType });

      const uploaded = await storageSource.uploadFile({
        file,
        fileName: names.storageFileName,
        path: storagePathFor(surface),
        metadata: {
          contentType: mimeType,
          // Recorded so "is this really 1024x1024?" is answerable from the Storage console without
          // downloading the object — the verification checklist leans on this.
          customMetadata: {
            appleSurface: surface,
            ...(output ? { width: String(output.width), height: String(output.height) } : {}),
          },
        },
      });

      const download = await storageSource.getDownloadURL(uploaded.path);
      if (!download.url) {
        throw new Error(
          "The file uploaded but Storage returned no download URL, so nothing was put on the record. Try again.",
        );
      }
      setValue({ fileName: names.documentFileName, imageUrl: download.url });
    },
    [setValue, storageSource, surface],
  );

  const handleFile = useCallback(
    async (file: File) => {
      releaseSource();
      setStage({ kind: "busy", note: "Reading the file…" });
      try {
        // ⚠️ ONLY THE HEADER IS READ HERE. The format has to be settled before anything expensive
        // happens: a HEIC, a WebP or a renamed HEIC is refused below, and an SVG never needs the
        // pixels at all. Reading the whole file first spent that cost on every one of those paths.
        const typeVerdict = evaluateUploadType(surface, await sniffedTypeOf(file));
        if (!typeVerdict.ok) {
          setStage({ kind: "refused", reason: typeVerdict.reason });
          return;
        }
        const confirmedType = typeVerdict.contentType;

        // ⚠️ THE SVG BYPASS. A vector logo has no raster dimensions to crop or check, so it is stored
        // as it arrived. Apple accepts SVG for a place-card logo and does not size-check it, and
        // neither does the backend.
        if (!requiresCrop(confirmedType)) {
          setStage({ kind: "busy", note: "Uploading the SVG…" });
          await uploadAndSet(file, "image/svg+xml", file.name);
          setStage({ kind: "idle" });
          return;
        }

        setStage({ kind: "busy", note: "Measuring the image…" });
        const loaded = await loadSource(surface, file, confirmedType);
        if ("refusal" in loaded) {
          setStage({ kind: "refused", reason: loaded.refusal });
          return;
        }
        sourceRef.current = loaded.source;
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedArea(null);
        // A new photo starts from the surface's default shape rather than inheriting the last one — the
        // previous choice was about a different image. A no-op for the three locked surfaces.
        setAspect(cropAspectFor(surface));
        setStage({ kind: "cropping", source: loaded.source });
      } catch (e) {
        setStage({
          kind: "refused",
          reason: `The file could not be read: ${e instanceof Error ? e.message : "unknown error"}. Nothing was uploaded.`,
        });
      } finally {
        // Let the same file be re-selected after a refusal.
        if (inputRef.current) inputRef.current.value = "";
      }
    },
    [releaseSource, surface, uploadAndSet],
  );

  const confirmCrop = useCallback(async () => {
    const source = sourceRef.current;
    const area = croppedArea;
    if (!source || !area) return;

    // Stays in `cropping` throughout, so the dialog does not flicker out to a spinner and back — and
    // so a failure below lands somewhere the staff member can act on it.
    setStage({ kind: "cropping", source, busyNote: "Cropping and uploading…" });
    try {
      const rendered = await renderCrop(surface, source, area);
      if ("refusal" in rendered) {
        // ⚠️ The source is NOT released and the dialog is NOT closed. Every message that reaches here
        // asks for an adjustment, so the crop box has to still be there to adjust.
        setStage({ kind: "cropping", source, notice: rendered.refusal });
        return;
      }
      await uploadAndSet(rendered.blob, rendered.mimeType, source.originalName, rendered.output);
      releaseSource();
      setStage({ kind: "idle" });
    } catch (e) {
      // Also kept open: an upload failure is often transient, and pressing the button again is the
      // obvious response. A retry re-uploads under a fresh object name, so nothing is overwritten.
      setStage({
        kind: "cropping",
        source,
        notice: `The crop could not be uploaded: ${e instanceof Error ? e.message : "unknown error"}. Nothing was put on the record — you can try again.`,
      });
    }
  }, [croppedArea, releaseSource, surface, uploadAndSet]);

  const cancelCrop = useCallback(() => {
    releaseSource();
    setStage({ kind: "idle" });
  }, [releaseSource]);

  // Two kinds of busy: `stage.busy` blocks the field itself (no dialog up), `cropBusy` is work
  // happening WITH the dialog open. Both must disable the attach button; only the second drives the
  // dialog's own loading state.
  const cropBusy = stage.kind === "cropping" && Boolean(stage.busyNote);
  const busy = stage.kind === "busy" || cropBusy;
  const hasValue = Boolean(value?.imageUrl);

  /**
   * The zoom ceiling for the dialog as currently framed.
   *
   * ⚠️ THE LOCKED SURFACES READ THE STORED VALUE VERBATIM, and that branch exists precisely so they do.
   * `LoadedSource.maxZoom` was computed once, at load, from the surface's single shape — for those three
   * it can never go stale, so it is used unchanged. A CHOICE surface can change shape after load, and a
   * ceiling computed for 4:3 is wrong for 2:1 (the headroom differs), so gallery recomputes it. Getting
   * this wrong in either direction is a real defect: too high lets the box select fewer pixels than
   * Apple's minimum, too low pins the slider below where it should reach.
   */
  const croppingSource = stage.kind === "cropping" ? stage.source : undefined;
  const dialogMaxZoom =
    croppingSource === undefined
      ? 1
      : aspectChoices.length === 0
        ? croppingSource.maxZoom
        : maxZoomFor(surface, croppingSource.size, aspect);

  return (
    <>
      <LabelWithIconAndTooltip
        icon={<ImageIcon size="small" />}
        propertyKey={propertyKey}
        title={property.name || label}
      />

      <div className="flex flex-col gap-2 rounded-md border border-slate-200 p-3 dark:border-slate-700">
        <Typography variant="caption" className="text-text-secondary dark:text-text-secondary-dark">
          Apple requires {requirementFor(surface)}. Attach a{" "}
          {acceptedFilesFor(surface)
            .map((type) => type.replace("image/", "").replace("svg+xml", "SVG").toUpperCase())
            .join(" or ")}{" "}
          and frame the crop yourself — this console will not enlarge a small image to fit, and it
          will not convert one format to another.
          {surface === "placecardLogo" &&
            " An SVG is uploaded as-is with no crop step: a vector logo has no fixed pixel size, so there is nothing to crop or measure."}
        </Typography>

        {hasValue && (
          <div className="flex flex-col gap-1 rounded bg-slate-50 p-2 dark:bg-slate-800">
            <Typography variant="body2" className="font-mono text-sm">
              {value?.fileName}
            </Typography>
            <a
              href={value?.imageUrl}
              target="_blank"
              rel="noreferrer"
              className="break-all text-xs text-blue-600 underline dark:text-blue-400"
            >
              {value?.imageUrl}
            </a>
          </div>
        )}

        {stage.kind === "refused" && (
          <Alert color="error" size="small" onDismiss={() => setStage({ kind: "idle" })}>
            {stage.reason}
          </Alert>
        )}

        {stage.kind === "busy" && (
          <div className="flex items-center gap-2">
            <CircularProgress size="small" />
            <Typography variant="caption">{stage.note}</Typography>
          </div>
        )}

        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            // ⚠️ A BROWSER HINT ONLY — the bytes are sniffed and re-checked in `handleFile`.
            accept={accepted}
            disabled={disabled || busy}
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
          <Button
            variant="outlined"
            size="small"
            disabled={disabled || busy}
            onClick={() => inputRef.current?.click()}
          >
            {hasValue ? `Replace ${label}` : `Attach ${label}`}
          </Button>
          {hasValue && (
            <Button
              variant="text"
              size="small"
              color="error"
              disabled={disabled || busy}
              onClick={() => setValue(null)}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      <FieldHelperText
        includeDescription={includeDescription}
        showError={showError}
        error={error}
        property={property}
      />

      <Dialog
        open={stage.kind === "cropping"}
        maxWidth="4xl"
        fullWidth
        // ⚠️ Only on CLOSE, and not while work is in flight. `onOpenChange` is a two-way channel, so
        // handing it `cancelCrop` directly would release the source the instant the dialog OPENED;
        // and an Escape keypress mid-upload would close the bitmap out from under `renderCrop`.
        onOpenChange={(open) => {
          if (!open && !cropBusy) cancelCrop();
        }}
      >
        <DialogTitle variant="h6">Frame the {label}</DialogTitle>
        <DialogContent>
          {stage.kind === "cropping" && (
            <div className="flex flex-col gap-3">
              {/* The refusal for a crop that could not be used, shown WHERE it can be acted on. */}
              {stage.notice && (
                <Alert color="error" size="small">
                  {stage.notice}
                </Alert>
              )}
              {/*
                ⚠️ RENDERED ONLY FOR A SURFACE WITH CHOICES, which is what keeps the cover photo, the
                logo and the showcase image aspect-LOCKED exactly as they were. Apple fixes one shape
                for each of those, so a picker there would offer staff a way to produce a file Apple
                refuses.
              */}
              {aspectChoices.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <Typography variant="caption" className="w-16 shrink-0">
                    Shape
                  </Typography>
                  {aspectChoices.map((choice) => (
                    <Button
                      key={choice.label}
                      size="small"
                      // The framed shape is the selected one. Compared with a tolerance rather than
                      // `===` because these are derived fractions (16/9, 2/3), not literals.
                      variant={Math.abs(choice.aspect - aspect) < 1e-6 ? "filled" : "outlined"}
                      disabled={cropBusy}
                      onClick={() => selectAspect(choice.aspect)}
                    >
                      {choice.label}
                    </Button>
                  ))}
                </div>
              )}
              <div className="relative h-[26rem] w-full overflow-hidden rounded bg-black">
                <Cropper
                  image={stage.source.previewUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={aspect}
                  minZoom={1}
                  maxZoom={dialogMaxZoom}
                  showGrid
                  objectFit="contain"
                  restrictPosition
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_area, croppedAreaPixels) => setCroppedArea(croppedAreaPixels)}
                />
              </div>

              <div className="flex items-center gap-3">
                <Typography variant="caption" className="w-16 shrink-0">
                  Zoom
                </Typography>
                <Slider
                  min={1}
                  max={dialogMaxZoom}
                  step={0.01}
                  value={[zoom]}
                  // A source only just above Apple's minimum has NO zoom headroom, and a slider
                  // whose min equals its max is a control that lies about being adjustable.
                  disabled={dialogMaxZoom <= 1}
                  onValueChange={([next]) => setZoom(next)}
                />
              </div>

              <Typography
                variant="caption"
                className="text-text-secondary dark:text-text-secondary-dark"
              >
                Original {stage.source.size.width}x{stage.source.size.height}.{" "}
                {describeExport(
                  surface,
                  stage.source.sniffed,
                  resolveOutputSize(
                    surface,
                    normalizeCropRect(
                      croppedArea ?? {
                        x: 0,
                        y: 0,
                        width: stage.source.size.width,
                        height: stage.source.size.height,
                      },
                      stage.source.size,
                    ),
                  ),
                )}{" "}
                The zoom stops where the selection would fall below Apple&apos;s minimum.
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          {stage.kind === "cropping" && stage.busyNote && (
            <div className="mr-auto flex items-center gap-2">
              <CircularProgress size="small" />
              <Typography variant="caption">{stage.busyNote}</Typography>
            </div>
          )}
          <Button variant="text" disabled={cropBusy} onClick={cancelCrop}>
            Cancel
          </Button>
          <LoadingButton
            variant="filled"
            loading={cropBusy}
            disabled={cropBusy}
            onClick={() => void confirmCrop()}
          >
            {stage.kind === "cropping" && stage.notice ? "Try again" : "Crop and upload"}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * Sniffs the header, reading ONLY the opening bytes rather than the whole file.
 *
 * `Blob.slice` is lazy — it hands back a view, so the `arrayBuffer()` below transfers at most
 * `SNIFF_BYTES`. That matters because this runs before any validation: the answer decides whether the
 * file is refused outright, uploaded untouched as an SVG, or decoded for cropping, and only the last
 * of those needs the rest of the bytes.
 */
const sniffedTypeOf = async (file: File): Promise<string | null> => {
  const head = await file.slice(0, SNIFF_BYTES).arrayBuffer();
  return sniffImageMimeType(new Uint8Array(head));
};
